import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Link from "next/link";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { Github } from "lucide-react";
import { GitHubOrgSelector } from "../GitHubOrgSelector";
import { BlacksmithLogomarkSVG, BlacksmithWordmarkSVG } from "../BlacksmithLogoSVG";
import { CodesmithLottieIcon } from "@/components/icons/CodesmithLottieIcon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { useHasHoverPointer, useIsMobile } from "@/hooks/use-mobile";
import { isTypingTarget } from "@/lib/isTypingTarget";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { cn } from "@/lib/utils";
import { AppHeaderBar } from "@/components/ui/app-header";
import { CollapsedIconTooltip, RAIL_ROW, RAIL_ROW_ACTIVE, RailRowContents, SidebarAdminRow, SidebarGlobalFooter } from "./SidebarGlobalChrome";
import { SidebarBlockedOrgBanner, useSidebarGlobalBehaviors } from "./useSidebarGlobalBehaviors";
import { useActiveApp, isStandaloneAccountPath, isAdminPath, settingsPagePath } from "./useActiveApp";
import { getCachedLastActiveOrg, useCodesmithEnabledWithCache } from "./useCodesmithEnabledCache";
import { RAIL_WIDTH, RAIL_EXPANDED_WIDTH, RAIL_WIDTH_CSS_VAR, RAIL_WIDTH_TRANSITION_MS, RAIL_TRANSITION_EASING } from "./railConstants";

/**
 * Slim app-switcher rail pinned to the left edge of the app. Hosts the
 * Blacksmith logo, top-level app entries (GitHub Actions, Codesmith), and the
 * global footer controls (support, settings, notifications, org switcher).
 *
 * The rail is 48px wide by default; clicking the logo pins it open at 200px
 * (row labels, org selector, full wordmark) until the logo is clicked again —
 * it does not auto-collapse on hover-out or navigation. The expanded rail is
 * part of the page layout: the contextual sidebar and main content slide
 * over in lockstep with the panel's own width transition (see `layoutWidth`
 * below and the matching `left` transition on the contextual Sidebar), rather
 * than being covered by an overlay.
 */

// Master switch for the rail's whole-panel hover expansion. Off by default:
// the rail stays 48px and each row surfaces its own tooltip, while the
// vCPU/status widget expands as a standalone hover overlay (RailStatusHoverCard)
// rather than growing the whole rail. Flip to true to restore the original
// behavior where hovering expands the entire rail to reveal row labels. Typed
// as boolean so both code paths stay type-checked at the default value.
const RAIL_HOVER_EXPAND: boolean = false;

// Hover-intent dwell before expanding. The pointer must rest on the rail for
// this long before the wipe starts, so a quick pass-over (e.g. on the way to
// the main content) never kicks off a half-open-then-reverse animation. Close
// stays instant, so the rail still feels snappy.
const RAIL_OPEN_DELAY_MS = 200;

// The mutually-exclusive rail rows that can read as active.
type RailSection = "actions" | "codesmith" | "usage" | "admin";

// Slide/wipe transition for the expand/collapse. easeOutQuint (see
// RAIL_TRANSITION_EASING): the panel starts moving immediately and
// decelerates smoothly into its final width (an ease-out reads far smoother
// here than an ease-in-out, which hesitates then snaps). Duration/easing are
// shared with the contextual Sidebar's `left` transition (railConstants.ts)
// so the panel, the pushed main content, and the fixed-position contextual
// sidebar all slide in lockstep rather than one visibly lagging the others.
//
// This is an inline style, not a Tailwind class: Tailwind's content scanner
// only recognizes class strings that appear literally in source, and this
// value is assembled from imported constants, so a `[transition:...]`
// arbitrary-value class built from it would never get a matching CSS rule
// generated — the rail would silently skip the transition and jump straight
// to its new width. That's invisible on pages with a contextual Sidebar
// (its own `left` transition carries the visible slide), but on Codesmith
// pages, which push a plain flex sibling (SessionSidebar) with nothing else
// animating, it read as no transition at all.
const RAIL_TRANSITION_STYLE = `width ${RAIL_WIDTH_TRANSITION_MS}ms ${RAIL_TRANSITION_EASING}`;

// Set on navigation; hover expansion is ignored while true so the panel does
// not pop back open over the section the user just clicked into. Cleared once
// the pointer leaves the rail (a fresh hover re-enables expansion). Lives at
// module scope because the rail can still unmount and remount within a session
// (its Suspense boundary in SidebarLayout) and the flag must survive that
// remount.
let suppressExpandUntilPointerLeaves = false;

// Clears the post-navigation suppression once the pointer is actually off the
// rail. React's onMouseLeave can't be relied on for this: clicking a row
// collapses the panel (200→48px) instantly under a stationary cursor, and a
// resize out from under the pointer doesn't dispatch mouseleave — so the flag
// would otherwise stay armed and swallow the next genuine hover. A
// document-level mousemove checks the live pointer position instead.
//
// The listener lives at module scope, like the flag it clears: it must stay
// attached across any rail remount, because a mousemove missed in an
// instance-bound listener's teardown/re-arm gap could let the
// pointer travel off the rail unseen and then re-enter with the first
// delivered event already targeting the panel — leaving the flag stuck and
// hover dead until the pointer exits and re-enters. The rail is resolved from
// the DOM at event time (live rail or its suspense skeleton), so the check
// works whichever instance is mounted. If the rail leaves the page entirely,
// the first mousemove finds no rail, clears the flag, and removes the
// listener, so nothing lingers.
let suppressClearListener: ((ev: MouseEvent) => void) | null = null;
function removeSuppressClearListener() {
  if (suppressClearListener) {
    document.removeEventListener("mousemove", suppressClearListener);
    suppressClearListener = null;
  }
}

// Persists the logo-click pin (see `expanded`) across that same remount.
// Read once as the initial state on mount and written back on every change,
// so when the rail comes back after unmounting it resumes exactly how it was
// left, rather than resetting to collapsed each time.
let pinnedExpanded = false;
function armSuppressUntilPointerLeaves() {
  suppressExpandUntilPointerLeaves = true;
  removeSuppressClearListener();
  const check = (ev: MouseEvent) => {
    const target = ev.target as HTMLElement | null;
    if (target?.closest('[data-slot="app-switcher-rail"], [data-slot="app-switcher-rail-skeleton"]')) {
      return;
    }
    suppressExpandUntilPointerLeaves = false;
    removeSuppressClearListener();
  };
  suppressClearListener = check;
  document.addEventListener("mousemove", check);
}
export default function AppSwitcherRail() {
  const $ = _c(127);
  const router = useRouter();
  const activeApp = useActiveApp();
  const routeSection = isAdminPath(router.pathname) ? "admin" : router.pathname === "/[org]/usage" || router.pathname === "/usage" ? "usage" : isStandaloneAccountPath(router.pathname) ? null : activeApp === "codesmith" ? "codesmith" : "actions";
  const [optimisticSection, setOptimisticSection] = useState(null);
  const activeSection = optimisticSection ?? routeSection;
  const railRowNavigationRef = useRef(false);
  useIsMobile();
  const hasHoverPointer = useHasHoverPointer();
  const {
    activeGithubOrg,
    codesmithAutomationsEnabled,
    orgsResolved
  } = useActiveGithubOrgForUserNonSuspense();
  const codesmithEnabled = useCodesmithEnabledWithCache() !== false;
  const urlOrg = router.query.org;
  let t0;
  if ($[0] !== activeGithubOrg || $[1] !== orgsResolved || $[2] !== urlOrg) {
    t0 = (typeof urlOrg === "string" ? urlOrg : undefined) || activeGithubOrg || (orgsResolved ? undefined : getCachedLastActiveOrg());
    $[0] = activeGithubOrg;
    $[1] = orgsResolved;
    $[2] = urlOrg;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const currentOrg = t0;
  const runsJobsHref = currentOrg ? `/${currentOrg}/runs/jobs` : "/runs/jobs";
  let t1;
  if ($[4] !== currentOrg || $[5] !== router || $[6] !== runsJobsHref) {
    t1 = () => {
      if (currentOrg) {
        router.prefetch(runsJobsHref);
      }
    };
    $[4] = currentOrg;
    $[5] = router;
    $[6] = runsJobsHref;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const prefetchRunsJobs = t1;
  const {
    activeGithubOrgBlocked: globalOrgBlocked,
    gPrefixActiveRef
  } = useSidebarGlobalBehaviors();
  const [expanded, setExpanded] = useState(pinnedExpanded);
  const slim = !expanded;
  const layoutWidth = expanded ? RAIL_EXPANDED_WIDTH : RAIL_WIDTH;
  const [instantClose, setInstantClose] = useState(false);
  const [codesmithRowHovered, setCodesmithRowHovered] = useState(false);
  const panelRef = useRef(null);
  const openTimerRef = useRef(null);
  const portalMouseListenerRef = useRef(null);
  const portalObserverRef = useRef(null);
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
      }
    };
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const clearOpenTimer = t2;
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (portalMouseListenerRef.current) {
        document.removeEventListener("mousemove", portalMouseListenerRef.current);
        portalMouseListenerRef.current = null;
      }
      if (portalObserverRef.current) {
        portalObserverRef.current.disconnect();
        portalObserverRef.current = null;
      }
    };
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const removePortalTracker = t3;
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      removePortalTracker();
      const checkMouse = ev => {
        const target = ev.target as HTMLElement | null;
        if (!target) {
          return;
        }
        const overPanel = panelRef.current?.contains(target);
        const overPortal = target.closest("[data-radix-popper-content-wrapper]");
        const portalOpen = document.querySelector("[data-radix-popper-content-wrapper]");
        if (!overPanel && !overPortal && !portalOpen) {
          setExpanded(false);
          removePortalTracker();
        }
      };
      portalMouseListenerRef.current = checkMouse;
      document.addEventListener("mousemove", checkMouse);
      const observer = new MutationObserver(() => {
        if (document.querySelector("[data-radix-popper-content-wrapper]")) {
          return;
        }
        if (!panelRef.current?.matches(":hover")) {
          setExpanded(false);
        }
        removePortalTracker();
      });
      observer.observe(document.body, {
        childList: true
      });
      portalObserverRef.current = observer;
    };
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const installPortalTracker = t4;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = section => e => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      setOptimisticSection(section);
      railRowNavigationRef.current = true;
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleRailRowClick = t5;
  let t6;
  if ($[12] !== expanded || $[13] !== hasHoverPointer) {
    t6 = () => {
      if (!RAIL_HOVER_EXPAND) {
        return;
      }
      if (!hasHoverPointer || suppressExpandUntilPointerLeaves) {
        return;
      }
      removePortalTracker();
      if (expanded) {
        return;
      }
      clearOpenTimer();
      openTimerRef.current = setTimeout(() => {
        openTimerRef.current = null;
        if (!hasHoverPointer || suppressExpandUntilPointerLeaves) {
          return;
        }
        if (!panelRef.current?.matches(":hover")) {
          return;
        }
        setExpanded(true);
      }, RAIL_OPEN_DELAY_MS);
    };
    $[12] = expanded;
    $[13] = hasHoverPointer;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleMouseEnter = t6;
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = e_0 => {
      if (!RAIL_HOVER_EXPAND) {
        return;
      }
      clearOpenTimer();
      suppressExpandUntilPointerLeaves = false;
      removeSuppressClearListener();
      const related = e_0.relatedTarget;
      if (related instanceof HTMLElement && related.closest("[data-radix-popper-content-wrapper]")) {
        installPortalTracker();
        return;
      }
      if (document.querySelector("[data-radix-popper-content-wrapper]")) {
        installPortalTracker();
        return;
      }
      setExpanded(false);
    };
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleMouseLeave = t7;
  let t8;
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => () => {
      removePortalTracker();
      clearOpenTimer();
    };
    t9 = [removePortalTracker, clearOpenTimer];
    $[16] = t8;
    $[17] = t9;
  } else {
    t8 = $[16];
    t9 = $[17];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[18] !== router.events) {
    t10 = () => {
      const handleRouteChangeStart = () => {
        const railRowNavigation = railRowNavigationRef.current;
        railRowNavigationRef.current = false;
        if (railRowNavigation && panelRef.current?.matches(":hover")) {
          armSuppressUntilPointerLeaves();
        }
        clearOpenTimer();
        removePortalTracker();
      };
      router.events.on("routeChangeStart", handleRouteChangeStart);
      return () => {
        router.events.off("routeChangeStart", handleRouteChangeStart);
      };
    };
    t11 = [router.events, removePortalTracker, clearOpenTimer];
    $[18] = router.events;
    $[19] = t10;
    $[20] = t11;
  } else {
    t10 = $[19];
    t11 = $[20];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[21] !== router.events) {
    t12 = () => {
      const settle = () => {
        if (RAIL_HOVER_EXPAND) {
          setInstantClose(true);
          setExpanded(false);
        }
        setOptimisticSection(null);
      };
      router.events.on("routeChangeComplete", settle);
      router.events.on("routeChangeError", settle);
      return () => {
        router.events.off("routeChangeComplete", settle);
        router.events.off("routeChangeError", settle);
      };
    };
    t13 = [router.events];
    $[21] = router.events;
    $[22] = t12;
    $[23] = t13;
  } else {
    t12 = $[22];
    t13 = $[23];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[24] !== instantClose) {
    t14 = () => {
      if (!instantClose) {
        return;
      }
      const id = requestAnimationFrame(() => setInstantClose(false));
      return () => cancelAnimationFrame(id);
    };
    t15 = [instantClose];
    $[24] = instantClose;
    $[25] = t14;
    $[26] = t15;
  } else {
    t14 = $[25];
    t15 = $[26];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[27] !== expanded) {
    t16 = () => {
      pinnedExpanded = expanded;
    };
    t17 = [expanded];
    $[27] = expanded;
    $[28] = t16;
    $[29] = t17;
  } else {
    t16 = $[28];
    t17 = $[29];
  }
  useLayoutEffect(t16, t17);
  let t18;
  let t19;
  if ($[30] !== layoutWidth) {
    t18 = () => {
      document.documentElement.style.setProperty(RAIL_WIDTH_CSS_VAR, `${layoutWidth}px`);
    };
    t19 = [layoutWidth];
    $[30] = layoutWidth;
    $[31] = t18;
    $[32] = t19;
  } else {
    t18 = $[31];
    t19 = $[32];
  }
  useLayoutEffect(t18, t19);
  let t20;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = [];
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  useLayoutEffect(_temp2, t20);
  let t21;
  let t22;
  if ($[34] !== codesmithEnabled || $[35] !== currentOrg || $[36] !== router) {
    t21 = () => {
      if (!codesmithEnabled) {
        return;
      }
      const handleNewChat = e_1 => {
        if (e_1.altKey && e_1.shiftKey && !e_1.metaKey && !e_1.ctrlKey && e_1.code === "KeyN") {
          e_1.preventDefault();
          const sessionsPath = currentOrg ? `/${currentOrg}/sessions/new` : "/sessions/new";
          router.push(sessionsPath);
        }
      };
      document.addEventListener("keydown", handleNewChat);
      return () => document.removeEventListener("keydown", handleNewChat);
    };
    t22 = [codesmithEnabled, currentOrg, router];
    $[34] = codesmithEnabled;
    $[35] = currentOrg;
    $[36] = router;
    $[37] = t21;
    $[38] = t22;
  } else {
    t21 = $[37];
    t22 = $[38];
  }
  useEffect(t21, t22);
  let t23;
  let t24;
  if ($[39] !== codesmithAutomationsEnabled || $[40] !== currentOrg || $[41] !== router) {
    t23 = () => {
      if (!codesmithAutomationsEnabled) {
        return;
      }
      const handleAutomations = e_2 => {
        if (e_2.altKey && e_2.shiftKey && !e_2.metaKey && !e_2.ctrlKey && e_2.code === "KeyA") {
          e_2.preventDefault();
          const automationsPath = currentOrg ? `/${currentOrg}/automations` : "/automations";
          router.push(automationsPath);
        }
      };
      document.addEventListener("keydown", handleAutomations);
      return () => document.removeEventListener("keydown", handleAutomations);
    };
    t24 = [codesmithAutomationsEnabled, currentOrg, router];
    $[39] = codesmithAutomationsEnabled;
    $[40] = currentOrg;
    $[41] = router;
    $[42] = t23;
    $[43] = t24;
  } else {
    t23 = $[42];
    t24 = $[43];
  }
  useEffect(t23, t24);
  let t25;
  let t26;
  if ($[44] !== codesmithEnabled || $[45] !== currentOrg || $[46] !== gPrefixActiveRef || $[47] !== router) {
    t25 = () => {
      if (!codesmithEnabled) {
        return;
      }
      const handleCodesmith = e_3 => {
        if (isTypingTarget(e_3.target)) {
          return;
        }
        if (e_3.key === "c" && !e_3.metaKey && !e_3.ctrlKey && !e_3.altKey && !e_3.shiftKey && !gPrefixActiveRef.current) {
          e_3.preventDefault();
          const sessionsPath_0 = currentOrg ? `/${currentOrg}/sessions/new` : "/sessions/new";
          router.push(sessionsPath_0);
        }
      };
      document.addEventListener("keydown", handleCodesmith);
      return () => document.removeEventListener("keydown", handleCodesmith);
    };
    t26 = [codesmithEnabled, currentOrg, gPrefixActiveRef, router];
    $[44] = codesmithEnabled;
    $[45] = currentOrg;
    $[46] = gPrefixActiveRef;
    $[47] = router;
    $[48] = t25;
    $[49] = t26;
  } else {
    t25 = $[48];
    t26 = $[49];
  }
  useEffect(t25, t26);
  const t27 = currentOrg ?? undefined;
  let t28;
  if ($[50] !== globalOrgBlocked || $[51] !== t27) {
    t28 = <SidebarBlockedOrgBanner activeGithubOrgBlocked={globalOrgBlocked} currentOrg={t27} />;
    $[50] = globalOrgBlocked;
    $[51] = t27;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  const t29 = expanded ? "expanded" : "collapsed";
  let t30;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = cn("group relative h-full shrink-0 flex flex-col overflow-hidden", "bg-sidebar shadow-[inset_-1px_0_0_0_var(--color-border)]");
    $[53] = t30;
  } else {
    t30 = $[53];
  }
  const t31 = instantClose ? "none" : RAIL_TRANSITION_STYLE;
  let t32;
  if ($[54] !== layoutWidth || $[55] !== t31) {
    t32 = {
      width: layoutWidth,
      transition: t31
    };
    $[54] = layoutWidth;
    $[55] = t31;
    $[56] = t32;
  } else {
    t32 = $[56];
  }
  const t33 = expanded ? "Collapse sidebar" : "Expand sidebar";
  let t34;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = () => setExpanded(_temp3);
    $[57] = t34;
  } else {
    t34 = $[57];
  }
  const t35 = expanded ? "Collapse sidebar" : "Expand sidebar";
  const t36 = expanded ? "opacity-0" : "opacity-100";
  const t37 = instantClose ? "transition-none" : "[transition:opacity_100ms_ease-out]";
  let t38;
  if ($[58] !== t36 || $[59] !== t37) {
    t38 = cn("flex size-8 shrink-0 items-center justify-center", t36, t37);
    $[58] = t36;
    $[59] = t37;
    $[60] = t38;
  } else {
    t38 = $[60];
  }
  let t39;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <div className="h-4 w-6 text-[#0c0c0de5] dark:text-blacksmith-yellow"><BlacksmithLogomarkSVG /></div>;
    $[61] = t39;
  } else {
    t39 = $[61];
  }
  let t40;
  if ($[62] !== t38) {
    t40 = <div className={t38}>{t39}</div>;
    $[62] = t38;
    $[63] = t40;
  } else {
    t40 = $[63];
  }
  let t41;
  if ($[64] !== expanded || $[65] !== instantClose) {
    t41 = <div aria-hidden={true} className="pointer-events-none absolute left-[6px] top-1/2 -translate-y-1/2"><BlacksmithWordmarkSVG expanded={expanded} instant={instantClose} className="h-4 w-auto origin-left scale-[1.0485] text-[#0c0c0de5] dark:text-blacksmith-yellow" /></div>;
    $[64] = expanded;
    $[65] = instantClose;
    $[66] = t41;
  } else {
    t41 = $[66];
  }
  let t42;
  if ($[67] !== t35 || $[68] !== t40 || $[69] !== t41) {
    t42 = <button type="button" onClick={t34} aria-label={t35} className="relative flex h-8 w-full items-center hover:opacity-70 transition-opacity cursor-pointer">{t40}{t41}</button>;
    $[67] = t35;
    $[68] = t40;
    $[69] = t41;
    $[70] = t42;
  } else {
    t42 = $[70];
  }
  let t43;
  if ($[71] !== t33 || $[72] !== t42) {
    t43 = <AppHeaderBar><CollapsedIconTooltip label={t33} enabled={true}>{t42}</CollapsedIconTooltip></AppHeaderBar>;
    $[71] = t33;
    $[72] = t42;
    $[73] = t43;
  } else {
    t43 = $[73];
  }
  let t44;
  if ($[74] !== expanded) {
    t44 = <div className="pt-app-header-gap"><GitHubOrgSelector expanded={expanded} /></div>;
    $[74] = expanded;
    $[75] = t44;
  } else {
    t44 = $[75];
  }
  const t45 = currentOrg ? true : null;
  let t46;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = handleRailRowClick("actions");
    $[76] = t46;
  } else {
    t46 = $[76];
  }
  const t47 = activeSection === "actions" ? "page" : undefined;
  const t48 = activeSection === "actions" && RAIL_ROW_ACTIVE;
  let t49;
  if ($[77] !== t48) {
    t49 = cn(RAIL_ROW, t48);
    $[77] = t48;
    $[78] = t49;
  } else {
    t49 = $[78];
  }
  let t50;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <RailRowContents icon={<Github className="size-[18px]" />} label="GitHub Actions" />;
    $[79] = t50;
  } else {
    t50 = $[79];
  }
  let t51;
  if ($[80] !== prefetchRunsJobs || $[81] !== runsJobsHref || $[82] !== t45 || $[83] !== t47 || $[84] !== t49) {
    t51 = <Link href={runsJobsHref} prefetch={t45} onMouseEnter={prefetchRunsJobs} onFocus={prefetchRunsJobs} onClick={t46} aria-current={t47} className={t49}>{t50}</Link>;
    $[80] = prefetchRunsJobs;
    $[81] = runsJobsHref;
    $[82] = t45;
    $[83] = t47;
    $[84] = t49;
    $[85] = t51;
  } else {
    t51 = $[85];
  }
  let t52;
  if ($[86] !== slim || $[87] !== t51) {
    t52 = <CollapsedIconTooltip label="GitHub Actions" enabled={slim}>{t51}</CollapsedIconTooltip>;
    $[86] = slim;
    $[87] = t51;
    $[88] = t52;
  } else {
    t52 = $[88];
  }
  let t53;
  if ($[89] !== activeSection || $[90] !== codesmithEnabled || $[91] !== codesmithRowHovered || $[92] !== currentOrg || $[93] !== slim) {
    t53 = codesmithEnabled && (() => {
      const codesmithRow = <Link href={currentOrg ? `/${currentOrg}/sessions/new` : "/sessions/new"} onMouseEnter={() => setCodesmithRowHovered(true)} onMouseLeave={() => setCodesmithRowHovered(false)} onClick={handleRailRowClick("codesmith")} aria-current={activeSection === "codesmith" ? "page" : undefined} className={cn(RAIL_ROW, activeSection === "codesmith" && RAIL_ROW_ACTIVE)}><RailRowContents icon={<CodesmithLottieIcon play={codesmithRowHovered} />} label="[code]smith" /></Link>;
      return <Tooltip><TooltipTrigger asChild={true}>{codesmithRow}</TooltipTrigger>{slim && <TooltipContent side="right" className="flex items-center gap-2"><span className="leading-none">[code]smith</span><ShortcutKeys keys={["C"]} /></TooltipContent>}</Tooltip>;
    })();
    $[89] = activeSection;
    $[90] = codesmithEnabled;
    $[91] = codesmithRowHovered;
    $[92] = currentOrg;
    $[93] = slim;
    $[94] = t53;
  } else {
    t53 = $[94];
  }
  let t54;
  if ($[95] !== t52 || $[96] !== t53) {
    t54 = <nav className="flex flex-col gap-1 pt-1" aria-label="Apps">{t52}{t53}</nav>;
    $[95] = t52;
    $[96] = t53;
    $[97] = t54;
  } else {
    t54 = $[97];
  }
  const t55 = activeSection === "admin";
  let t56;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = handleRailRowClick("admin");
    $[98] = t56;
  } else {
    t56 = $[98];
  }
  let t57;
  if ($[99] !== expanded || $[100] !== slim || $[101] !== t55) {
    t57 = <SidebarAdminRow expanded={expanded} active={t55} onClick={t56} hideLabelWhenCollapsed={slim} />;
    $[99] = expanded;
    $[100] = slim;
    $[101] = t55;
    $[102] = t57;
  } else {
    t57 = $[102];
  }
  let t58;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <div className="flex-1" />;
    $[103] = t58;
  } else {
    t58 = $[103];
  }
  const t59 = currentOrg ?? undefined;
  const t60 = activeApp === "codesmith";
  let t61;
  if ($[104] !== currentOrg || $[105] !== t60) {
    t61 = settingsPagePath(currentOrg, t60);
    $[104] = currentOrg;
    $[105] = t60;
    $[106] = t61;
  } else {
    t61 = $[106];
  }
  let t62;
  if ($[107] !== activeSection || $[108] !== expanded || $[109] !== slim || $[110] !== t59 || $[111] !== t61) {
    t62 = <SidebarGlobalFooter expanded={expanded} currentOrg={t59} settingsHref={t61} activeSection={activeSection} onSectionClick={handleRailRowClick} className="pb-2" hideLabelWhenCollapsed={slim} statusAsHoverCard={slim} />;
    $[107] = activeSection;
    $[108] = expanded;
    $[109] = slim;
    $[110] = t59;
    $[111] = t61;
    $[112] = t62;
  } else {
    t62 = $[112];
  }
  let t63;
  if ($[113] !== t43 || $[114] !== t44 || $[115] !== t54 || $[116] !== t57 || $[117] !== t62) {
    t63 = <div className="flex h-full w-full flex-col px-2">{t43}{t44}{t54}{t57}{t58}{t62}</div>;
    $[113] = t43;
    $[114] = t44;
    $[115] = t54;
    $[116] = t57;
    $[117] = t62;
    $[118] = t63;
  } else {
    t63 = $[118];
  }
  let t64;
  if ($[119] !== handleMouseEnter || $[120] !== t29 || $[121] !== t32 || $[122] !== t63) {
    t64 = <div ref={panelRef} data-slot="app-switcher-rail" data-state={t29} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={t30} style={t32}>{t63}</div>;
    $[119] = handleMouseEnter;
    $[120] = t29;
    $[121] = t32;
    $[122] = t63;
    $[123] = t64;
  } else {
    t64 = $[123];
  }
  let t65;
  if ($[124] !== t28 || $[125] !== t64) {
    t65 = <>{t28}{t64}</>;
    $[124] = t28;
    $[125] = t64;
    $[126] = t65;
  } else {
    t65 = $[126];
  }
  return t65;
}
function _temp3(prev) {
  return !prev;
}
function _temp2() {
  return _temp;
}
function _temp() {
  document.documentElement.style.removeProperty(RAIL_WIDTH_CSS_VAR);
}